import { Component, OnInit, ChangeDetectionStrategy, Input, OnDestroy, ElementRef, ChangeDetectorRef } from '@angular/core';
import { IPanelContext } from '../../models/IPanelContext';
import { RuleVioliationManagerService } from '../../services/rule-violiation-manager.service';
import { Subject } from 'rxjs';
import { AbstractControl, FormArray } from '@angular/forms';
import { RuleViolationDetailModel } from './ruleViolationDetailModel';
import { IModelContext } from '../../models/IModelContext';
import { IListPanelContext } from '../../models/IListPanelContext';
import { RuleViolationModel } from '../../../../common/models/RuleViolationModel';
import { v } from '@angular/core/src/render3';

@Component({
  selector: 'plr-rule-violation-details',
  templateUrl: './rule-violation-details.component.html',
  styleUrls: ['./rule-violation-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RuleViolationDetailsComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<boolean>();
  private _panels: IPanelContext[];
  private _formGroupContainer: AbstractControl;
  private initialized = false;
  private _mode = 'Expanded';
  private _active: RuleViolationDetailModel;
  private _violations: {};
  private _violationCount = 0;

  @Input()
  set panels(panels: IPanelContext[]) {
    this._panels = panels;
    this.refreshViolations();
  }

  @Input()
  set formGroupContainer(formGroupContainer: AbstractControl) {
    this._formGroupContainer = formGroupContainer;
    this.refreshViolations();
  }

  constructor(public _ruleViolationManager: RuleVioliationManagerService, private _changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit() {
    this._ruleViolationManager.ruleViolations$.takeUntil(this._destroyed$).subscribe(() => {
      this.refreshViolations();
    });
    this.initialized = true;
    this.refreshViolations();
  }

  ngOnDestroy() {
    this._destroyed$.next(true);
  }

  refreshViolations() {
    if (!this.initialized) {
      return;
    }
    this._violations = {};
    var foundActive = false;
    var violationCount = 0;
    if (this._ruleViolationManager.controlHasViolations(this._formGroupContainer)) {
      this.addObjectViolation();
      violationCount++;
    }
    this._panels
      .sort(p => p.displayOrder)
      .forEach(panel => {
        if (panel.formGroup && panel.formGroup.invalid) {
          if (panel.fieldContexts && panel.fieldContexts.length > 0) {
            panel.fieldContexts
              .sort(f => f.metadata.displayOrder)
              .forEach(field => {
                if (field.control && this._ruleViolationManager.controlHasViolations(field.control)) {
                  violationCount++;
                  this.addViolation(panel, field);
                  if (this._active && this._active.field === field) {
                    foundActive = true;
                  }
                }
              });
          } else {
            var listPanel = panel as IListPanelContext;
            if (listPanel.property) {
              var formArray = listPanel.formGroup.get(listPanel.property);
              if (formArray.invalid) {
                if (this._ruleViolationManager.controlHasViolations(formArray)) {
                  violationCount++;
                  this.addViolation(panel, null);
                }
                if (listPanel.metadata.simpleList && listPanel.itemContexts) {
                  var index = 0;
                  listPanel.itemContexts.forEach(item => {
                    var parentError = false;
                    item
                      .sort(f => f.metadata.displayOrder)
                      .forEach(field => {
                        if (field.control && this._ruleViolationManager.controlHasViolations(field.control)) {
                          violationCount++;
                          this.addViolation(panel, field, index);
                          if (this._active && this._active.field === field) {
                            foundActive = true;
                          }
                        } else if (field.control && field.control.parent && this._ruleViolationManager.controlHasViolations(field.control.parent) && !parentError) {
                          parentError = true;
                          violationCount++;
                          this.addViolation(panel, null, index, field.control.parent.errors['rule-violation'].message);
                        }
                      });
                    index++;
                  });
                } else {
                  var subEntityCount = this.getSubEntityErrorCount(listPanel);
                  var summaryMessage = subEntityCount > 1 ? subEntityCount + ' Sub entities with errors or warnings.' : '1 Sub entity with errors or warnings.';
                  violationCount++;
                  this.addViolation(listPanel, null, undefined, summaryMessage);
                  if (this._active && this._active.panel === panel && this._active.summaryMessage === summaryMessage) {
                    foundActive = true;
                  }
                }
              }
            }
          }
        }
      });
    if (this._ruleViolationManager.rootViolations.length > 0) {
      violationCount++;
      var warnings = this._ruleViolationManager.rootViolations.filter(rv => rv.violationType == 'Warning');
      var errors = this._ruleViolationManager.rootViolations.filter(rv => rv.violationType == 'Error');

      if(errors.length > 0){
        if (!this._violations.hasOwnProperty('Error')) {
          this._violations['Error'] = [];
        }
        this._violations['Error'].push(new RuleViolationDetailModel(null, null, null, errors, null));
      }
  
      if(warnings.length > 0){
        if (!this._violations.hasOwnProperty('Warning')) {
          this._violations['Warning'] = [];
        }
        this._violations['Warning'].push(new RuleViolationDetailModel(null, null, null, warnings, null));
      }
    }
    this._violationCount = violationCount;
    if (this._violationCount > 0) {
      this.expand();
    } else {
      this.collapse();
    }
    this._changeDetectorRef.markForCheck();
  }

  private getSubEntityErrorCount(panel: IListPanelContext): Number {
    var count = 0;
    var formArray = panel.formGroup.get(panel.property) as FormArray;
    formArray.controls.forEach(c => {
      if (c.invalid) {
        count++;
      }
    });
    return count;
  }

  private addViolation(panel: IPanelContext, field: IModelContext, index: number = undefined, summaryMessage?: string) {
    if (!this._violations.hasOwnProperty(panel.displayName)) {
      this._violations[panel.displayName] = [];
    }
    this._violations[panel.displayName].push(new RuleViolationDetailModel(panel, index, field, field ? this._ruleViolationManager.getRuleViolations(field.control) : null, summaryMessage));
  }

  private addObjectViolation() {
    var warnings = this._ruleViolationManager.getRuleViolations(this._formGroupContainer).filter(rv => rv.violationType == 'Warning');
    var errors = this._ruleViolationManager.getRuleViolations(this._formGroupContainer).filter(rv => rv.violationType == 'Error');      

    if(errors.length > 0){
      if (!this._violations.hasOwnProperty('Error')) {
        this._violations['Error'] = [];
      }
      this._violations['Error'].push(new RuleViolationDetailModel(null, null, null, errors, null));
    }

    if(warnings.length > 0){
      if (!this._violations.hasOwnProperty('Warning')) {
        this._violations['Warning'] = [];
      }
      this._violations['Warning'].push(new RuleViolationDetailModel(null, null, null, warnings, null));
    }
  }

  makeActive(detail: RuleViolationDetailModel): void {
    if (detail && this.scrollTo(detail.field ? (detail.field.control as any) : null, detail.panel)) {
      this._mode = 'Active';
      this._active = detail;
    }
  }

  scrollTo(control: AbstractControl, panel: IPanelContext): boolean {
    if (control && (control as any).elementRef) {
      this.scrollToElement((control as any).elementRef.nativeElement as HTMLElement);
      return true;
    }
    if (panel) {
      var panelElement = document.getElementById(panel.identifier);
      if (panelElement) {
        this.scrollToElement(panelElement);
        return true;
      }
    }
    return false;
  }

  private scrollToElement(nativeElement: HTMLElement): void {
    nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    nativeElement.focus();
  }

  objectProperties(object): string[] {
    return Object.keys(object);
  }

  expand(): void {
    this._mode = 'Expanded';
  }

  collapse(): void {
    this._mode = 'Collapsed';
  }

  next(): void {
    if (this._active) {
      var useNext = false;
      var found = false;
      var firstPanelName = undefined;
      for (var panelName in this._violations) {
        if (!firstPanelName) {
          firstPanelName = panelName;
        }
        if (useNext) {
          var violations = this._violations[panelName];
          if (violations && violations.length > 0) {
            this.makeActive(violations[0]);
            found = true;
          }
          break;
        }
        if (this._active.panel.displayName !== panelName) {
          continue;
        }
        var violations = this._violations[panelName];
        var index = violations.indexOf(this._active);
        if (index === violations.length - 1) {
          useNext = true;
        } else {
          this.makeActive(violations[index + 1]);
          found = true;
          break;
        }
      }
      if (useNext && !found && firstPanelName) {
        var violations = this._violations[firstPanelName];
        if (violations && violations.length > 0) {
          this.makeActive(violations[0]);
        }
      }
    }
  }

  previous(): void {
    if (this._active) {
      var useLast = false;
      var found = false;
      var previousPanelName = undefined;
      for (var panelName in this._violations) {
        if (this._active.panel.displayName === panelName) {
          var violations = this._violations[panelName];
          var index = violations.indexOf(this._active);
          if (index === 0) {
            if (previousPanelName) {
              var violations = this._violations[previousPanelName];
              if (violations && violations.length > 0) {
                this.makeActive(violations[0]);
                found = true;
                break;
              }
            } else {
              useLast = true;
            }
          } else {
            this.makeActive(violations[index - 1]);
            found = true;
            break;
          }
        }
        previousPanelName = panelName;
      }
      if (useLast && !found && previousPanelName) {
        var violations = this._violations[previousPanelName];
        if (violations && violations.length > 0) {
          this.makeActive(violations[violations.length - 1]);
        }
      }
    }
  }

  sectionHasWarningOnly(panelName : string): boolean {
    var index = this.objectProperties(this._violations).indexOf(panelName);
    return this._violations[Object.keys(this._violations)[index]].every(v => v.ruleViolations.every(rv => rv.violationType == 'Warning'));
  }

  fieldHasWarningOnly(fieldDetail : any) : boolean {
    if(Array.isArray(fieldDetail.control.errors["rule-violation"])){
      return fieldDetail.control.errors["rule-violation"].every(v => v.violationType == 'Warning');
    }
    else{
      return fieldDetail.control.errors["rule-violation"]["violationType"] == 'Warning';
    }    
  }
}



// WEBPACK FOOTER //
// ./src/app/pillar/workspace/components/rule-violation-details/rule-violation-details.component.ts